import { Controller } from '@hotwired/stimulus';
import { Dropdown } from 'bootstrap';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { grantGoogleMapsConsent, readGoogleMapsConsent } from '../consent/google_maps_consent.mjs';

const pinStyleClusterRenderer = {
    render({ count, position }, _stats, map) {
        const svg = `<svg fill="#212529" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240" width="50" height="50">
        <circle cx="120" cy="120" opacity="1" r="70" />
        <text x="50%" y="50%" style="fill:#ffffff" text-anchor="middle" font-size="50" dominant-baseline="middle" font-family="roboto,arial,sans-serif">${count}</text>
        </svg>`;
        const title = `Cluster of ${count} markers`;
        const zIndex = Number(google.maps.Marker.MAX_ZINDEX) + count;

        const parser = new DOMParser();
        const svgEl = parser.parseFromString(svg, 'image/svg+xml').documentElement;
        svgEl.setAttribute('transform', 'translate(0 25)');
        return new google.maps.marker.AdvancedMarkerElement({
            map,
            position,
            zIndex,
            title,
            content: svgEl,
        });
    },
};

export default class extends Controller {
    static targets = [
        'map',
        'locationList',
        'locationElement',
        'name',
        'address',
        'phone',
        'email',
        'companyCategory',
        'latitude',
        'longitude',
        'checkbox',
        'count',
        'dropdownToggle',
        'dropdownMenu',
        'searchInput',
        'searchBar',
        'clearSearch',
        'categoryFilterSearch',
        'categoryItemsContainer',
        'companyCategoryCheckbox',
        'companyCategoryCount',
        'companyCategoryDropdownToggle',
        'companyCategoryDropdownMenu',
        'companyCategoryFilterSearch',
        'companyCategoryItemsContainer',
        'mapConsent'
    ];

    static values = {
        options: Object
    }

    connect() {
        this.markers = [];
        this.activeItem = null;
        requestAnimationFrame(() => this.mobileCategories());

        this.boundHandleOutsideClick = this.handleOutsideClick.bind(this);
        document.addEventListener('click', this.boundHandleOutsideClick);

        this.boundMobileCategoriesResize = () => {
            clearTimeout(this._mobileCategoriesResizeTimer);
            this._mobileCategoriesResizeTimer = setTimeout(() => this.mobileCategories(), 100);
        };
        window.addEventListener('resize', this.boundMobileCategoriesResize);

        this.onInitialized = () => this.onConsentInitialized();
        this.onCmpEvent = () => this.onConsentEvent();
        window.addEventListener('UC_UI_INITIALIZED', this.onInitialized);
        window.addEventListener('UC_UI_CMP_EVENT', this.onCmpEvent);

        this.onConsentInitialized();

        this._activeFilter = 'none';
    }

    onConsentInitialized() {
        if (readGoogleMapsConsent()) {
            this.syncMapWithConsent();
            return;
        }
        if (this.consentRequested) {
            this.requestMapConsent();
        }
    }

    onConsentEvent() {
        this.consentRequested = false;
        if (readGoogleMapsConsent()) {
            this.syncMapWithConsent();
        }
    }

    async requestMapConsent(event) {
        if (event?.type === 'keydown') {
            event.preventDefault();
        }

        if (this.grantInFlight) {
            return;
        }

        const cmpMissing = typeof UC_UI === 'undefined';
        this.grantInFlight = true;
        try {
            const granted = await grantGoogleMapsConsent();
            if (!granted) {
                this.consentRequested = cmpMissing;
                return;
            }

            this.consentRequested = false;
            this.syncMapWithConsent();
        } finally {
            this.grantInFlight = false;
        }
    }

    syncMapWithConsent() {
        if (!readGoogleMapsConsent()) {
            return;
        }

        this.hideMapConsent();
        this.consentRequested = false;

        if (this.map) {
            return;
        }

        if (window.appMap) {
            this.map = window.appMap;
            this.markers = window.markers || [];
            this.markerClusterer = window.markerClusterer || null;
            return;
        }

        if (!window.google || !window.google.maps || !google.maps.marker?.AdvancedMarkerElement) {
            this.loadGoogleMapsScript();
            return;
        }

        this.initMap();
    }

    hideMapConsent() {
        if (!this.hasMapConsentTarget) {
            return;
        }
        const bannerHadFocus = this.mapConsentTarget.contains(document.activeElement);
        this.mapConsentTarget.hidden = true;
        if (bannerHadFocus) {
            this.mapTarget.tabIndex = -1;
            this.mapTarget.focus();
        }
    }

    get markersInstance() {
        if (!window.markers || !window.appMap) {
            console.warn("Markers not ready yet");
            return [];
        }
        return window.markers;
    }

    loadGoogleMapsScript() {
        if (!readGoogleMapsConsent() || document.getElementById('google-maps-location-overview')) {
            return;
        }

        window.initMap = this.initMap.bind(this);

        const script = document.createElement('script');
        script.id = 'google-maps-location-overview';
        script.src = this.optionsValue.baseMapUrl;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
    }

    initMap() {
        if (!readGoogleMapsConsent()) {
            return;
        }

        const { AdvancedMarkerElement } = google.maps.marker;

        if (!this.hasMapTarget) {
            console.error('Map target not found');
            return;
        }
        const isMobile = window.innerWidth < 576;

        this.map = new google.maps.Map(this.mapTarget, {
            center: isMobile ? { lat: 5, lng: 0 } : { lat: 0, lng: 0 },
            zoom: 2,
            mapId: this.optionsValue.baseMapId,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            tilt: 0,
            rotateControl: false,
            gestureHandling: "cooperative",
        });

        this.map.addListener("idle", () => {
            if (this.map.getTilt() !== 0) {
                this.map.setTilt(0);
            }
        });

        google.maps.event.addListenerOnce(this.map, 'idle', () => {
            if (isMobile) {
                const offsetY = this.mapTarget.offsetHeight * 0.1;
                this.map.panBy(0, -offsetY);
                this.map.setZoom(1);
                this.map.panBy(0, 40);
            }
        });

        window.appMap = this.map;

        const locations = this.extractLocationsFromDOM();

        locations.forEach((loc, index) => {
            const marker = new AdvancedMarkerElement({
                position: { lat: loc.lat, lng: loc.lng },
                title: loc.name,
                content: this.createDefaultPin(),
            });

            marker._index = index;
            marker._sidebarItem = loc.sidebarItem;

            marker.addListener('click', () => {
                this.map.panTo(marker.position);
                this.map.setZoom(12);
                this.highlightLocation(index);
            });

            if (loc.sidebarItem) {
                loc.sidebarItem.addEventListener('click', () => {
                    this.map.panTo(marker.position);
                    this.map.setZoom(12);
                    google.maps.event.trigger(marker, 'click');
                });
            }

            this.markers.push(marker);
        });

        this.markerClusterer = new MarkerClusterer({
            map: this.map,
            markers: this.markers,
            renderer: pinStyleClusterRenderer,
        });
        window.markers = this.markers;
        window.markerClusterer = this.markerClusterer;

        google.maps.event.addListenerOnce(this.map, "idle", () => {
            this.filterLocationsInView();

            this.map.addListener("bounds_changed", () => {
                if (!window.markers || window.markers.length === 0) return;
                this.filterLocationsInView();
            });
        });
    }

    createDefaultPin() {
        const pin = document.createElement('div');
        pin.className = 'map-pin';
        pin.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="44" viewBox="0 0 40 54" fill="none">
                <circle cx="20" cy="20" r="8" fill="white"/>
                <path d="M0 19.8271C0 8.81205 8.95833 0 20 0C31.0417 0 40 8.81205 40 19.8271C40 32.3108 27.5 47.3123 22.2917 52.9772C21.0417 54.3409 18.9583 54.3409 17.7083 52.9772C12.5 47.3123 0 32.3108 0 19.8271ZM20 26.8558C23.6458 26.8558 26.6667 23.8135 26.6667 20.1418C26.6667 16.4701 23.6458 13.4279 20 13.4279C16.3542 13.4279 13.3333 16.4701 13.3333 20.1418C13.3333 23.8135 16.3542 26.8558 20 26.8558Z" fill="#212529"/>
            </svg>
        `;
        return pin;
    }

    createActivePin() {
        const pin = document.createElement('div');
        pin.className = 'map-pin';
        pin.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="44" viewBox="0 0 40 54" fill="none">
                <circle cx="20" cy="20" r="8" fill="white"/>
                <path d="M0 19.8271C0 8.81205 8.95833 0 20 0C31.0417 0 40 8.81205 40 19.8271C40 32.3108 27.5 47.3123 22.2917 52.9772C21.0417 54.3409 18.9583 54.3409 17.7083 52.9772C12.5 47.3123 0 32.3108 0 19.8271ZM20 26.8558C23.6458 26.8558 26.6667 23.8135 26.6667 20.1418C26.6667 16.4701 23.6458 13.4279 20 13.4279C16.3542 13.4279 13.3333 16.4701 13.3333 20.1418C13.3333 23.8135 16.3542 26.8558 20 26.8558Z" fill="#E0251B"/>
            </svg>
        `;
        return pin;
    }

    filterLocationsInView() {
        const bounds = this.map.getBounds();
        if (!bounds) return;

        this.markers.forEach((marker) => {
            const sidebarItem = marker._sidebarItem;
            if (!marker || !marker.position || !sidebarItem) return;

            const lat = typeof marker.position.lat === 'function' ? marker.position.lat() : marker.position.lat;
            const lng = typeof marker.position.lng === 'function' ? marker.position.lng() : marker.position.lng;
            const latLng = new google.maps.LatLng(lat, lng);
            const isVisible = bounds.contains(latLng);

            const isExcluded = sidebarItem.classList.contains('search-excluded') || sidebarItem.classList.contains('filter-excluded');
            if(isVisible && !isExcluded){
                sidebarItem.style.display = 'block';
            }else{
                sidebarItem.style.display = 'none';
            }
        });

        this.updateNoLocationsMessage();
    }

    filterLocationsBySearch(event) {
        const searchTerm = event.target.value.toLowerCase();
        let visibleCount = 0;

        this.locationElementTargets.forEach((item, index) => {
            const name = item.querySelector('[data-location-new-map-target="name"]')?.textContent.toLowerCase() || '';
            const address = item.querySelector('[data-location-new-map-target="address"]')?.textContent.toLowerCase() || '';
            const companyCategory = item.querySelector('[data-location-new-map-target="companyCategory"]')?.textContent.toLowerCase() || '';
            const email = item.querySelector('[data-location-new-map-target="email"]')?.textContent.toLowerCase() || '';
            const phone = item.querySelector('[data-location-new-map-target="phone"]')?.textContent.toLowerCase() || '';

            const matches =
            this.matchesField(name, searchTerm) ||
            this.matchesField(address, searchTerm) ||
            this.matchesField(companyCategory, searchTerm) ||
            this.matchesField(email, searchTerm) ||
            this.matchesField(phone, searchTerm);
            const isExcluded = item.classList.contains('filter-excluded');
            item.style.display = item.style.display = (matches && !isExcluded) ? 'block' : 'none';
            item.classList.toggle('search-excluded', !matches);
            if (matches && !isExcluded) visibleCount++;
        });

        this.updateMarkerVisibility();
        const eventSource = event.detail?.source;
        if(visibleCount && eventSource !== 'highlight-location'){
            this.zoomOutMap();
        }
        this.updateNoLocationsMessage();
    }

    searchOpen() {
        this.searchBarTarget?.classList.add('location-search-active');
    }

    searchBlur() {
        const value = this.searchInputTarget.value.trim()
        if (value === "") {
            this.searchBarTarget?.classList.remove('location-search-active')
        }
    }


    reapplyLocationSearch() {
        if (!this.hasSearchInputTarget) {
            this.updateMarkerVisibility();
            return;
        }

        const searchTerm = this.searchInputTarget.value.trim();
        if (searchTerm) {
            this.filterLocationsBySearch({ target: this.searchInputTarget });
            return;
        }

        this.locationElementTargets.forEach((item) => {
            if (!item.classList.contains('filter-excluded')) {
                item.style.display = 'block';
            }
            item.classList.remove('search-excluded');
        });
        this.updateMarkerVisibility();
        this.updateNoLocationsMessage();
    }

    clearSearch() {
        this.searchInputTarget.value = "";
        this.searchInputTarget.blur();
        document.activeElement.blur();

        this.locationElementTargets.forEach(item => {
            if(!item.classList.contains('filter-excluded')){
                item.style.display = 'block';
            }
            item.classList.remove('search-excluded');
            item.classList.remove("location-active");
        });
        this.updateMarkerVisibility();
        this.zoomOutMap();
        this.updateNoLocationsMessage();

        const markersToReset = Array.isArray(window.markers) ? window.markers : [];

        markersToReset.forEach(marker => {
            marker.content = this.createDefaultPin();
            marker.zIndex = 1;
        });
    }

    highlightLocation(index) {
        const items = document.querySelectorAll('.company-location');

        if (this.activeItem !== null && items[this.activeItem]) {
            items[this.activeItem].classList.remove('location-active');
        }

        if (items[index]) {
            items[index].classList.add('location-active');
            const locationName = items[index].querySelector('[data-location-new-map-target="name"]');
            if(locationName){
                this.searchInputTarget.value = locationName.textContent;
                this.searchInputTarget.dispatchEvent(new CustomEvent('input', {
                    detail: {
                        source: 'highlight-location',
                    },
                }));
                this.searchInputTarget.focus();
            }
            this.activeItem = index;
        }

        this.markers.forEach((marker, i) => {
            if (i === index) {
                marker.content = this.createActivePin();
                marker.zIndex = 1000;
            } else {
                marker.content = this.createDefaultPin();
                marker.zIndex = 1;
            }
        });
    }

    extractLocationsFromDOM() {
        const elements = document.querySelectorAll('.company-location');
        const locations = [];

        elements.forEach((el, i) => {
            const name = el.querySelector('.location-heading h5')?.textContent.trim() || '';
            const address = el.querySelector('.location-address p')?.textContent.trim() || '';
            const phone = el.querySelector('.location-phone a')?.getAttribute('href')?.replace('tel:', '').trim() || '';
            const email = el.querySelector('.location-email a')?.getAttribute('href')?.replace('mailto:', '').trim() || '';
            const latText = el.querySelector('.loc-lat')?.textContent.replace(/[^\d.-]/g, '').trim();
            const lngText = el.querySelector('.loc-long')?.textContent.replace(/[^\d.-]/g, '').trim();

            const lat = latText ? parseFloat(latText) : NaN;
            const lng = lngText ? parseFloat(lngText) : NaN;

            if (isNaN(lat) || isNaN(lng)) {
                return;
            }

            locations.push({ name, address, phone, email, lat, lng, sidebarItem: el });
        });

        return locations;
    }

    toggle(event) {
        if (event.target.tagName === 'INPUT') return;

        const checkbox = event.currentTarget.querySelector('input[type="checkbox"]');
        if (!checkbox) return;

        checkbox.checked = !checkbox.checked;
        event.currentTarget.classList.toggle('checked', checkbox.checked);

        if (event.currentTarget.classList.contains('company-category-item')) {
            this.updateCompanyCategoryCount();
        } else {
            this.updateCount();
        }
    }

    getProductAllowedCompanyIds() {
        const ids = [];
        if (!this.hasCheckboxTarget) {
            return ids;
        }
        this.checkboxTargets
            .filter((cb) => cb.checked)
            .forEach((cb) => {
                try {
                    const parsed = JSON.parse(cb.dataset.productCompanyIds);
                    if (Array.isArray(parsed)) {
                        ids.push(...parsed);
                    }
                } catch (e) {
                    console.warn("Invalid data-product-company-ids");
                }
            });
        return [...new Set(ids.map((id) => parseInt(id, 10)))];
    }

    getCompanyCategoryAllowedCompanyIds() {
        const ids = [];
        if (!this.hasCompanyCategoryCheckboxTarget) {
            return ids;
        }
        this.companyCategoryCheckboxTargets
            .filter((cb) => cb.checked)
            .forEach((cb) => {
                try {
                    const parsed = JSON.parse(cb.dataset.companyIds);
                    if (Array.isArray(parsed)) {
                        ids.push(...parsed);
                    }
                } catch (e) {
                    console.warn("Invalid data-company-ids");
                }
            });
        return [...new Set(ids.map((id) => parseInt(id, 10)))];
    }


    applyCombinedLocationFilter() {
        const productIds = this.getProductAllowedCompanyIds();
        const categoryIds = this.getCompanyCategoryAllowedCompanyIds();

        /** @type {number[]|null} */
        let allowedCompanyIds = null;

        if (productIds.length > 0 && categoryIds.length > 0) {
            const productSet = new Set(productIds);
            allowedCompanyIds = categoryIds.filter((id) => productSet.has(id));
        } else if (productIds.length > 0) {
            allowedCompanyIds = productIds;
        } else if (categoryIds.length > 0) {
            allowedCompanyIds = categoryIds;
        }

        this.locationElementTargets.forEach((el) => {
            const companyId = parseInt(el.dataset.companyId, 10);
            const allowed =
                allowedCompanyIds === null || allowedCompanyIds.includes(companyId);
            el.style.display = allowed ? '' : 'none';
            el.classList.toggle('filter-excluded', !allowed);
        });

        if (productIds.length > 0 && categoryIds.length > 0) {
            this._activeFilter = 'both';
        } else if (productIds.length > 0) {
            this._activeFilter = 'product';
        } else if (categoryIds.length > 0) {
            this._activeFilter = 'company';
        } else {
            this._activeFilter = 'none';
        }

        return allowedCompanyIds;
    }

    applyCategories() {
        this.clearSearch();
        if (this.hasCategoryFilterSearchTarget) {
            this.categoryFilterSearchTarget.value = '';
            this.filterCategoryItems();
        }

        const selected = this.checkboxTargets.filter(cb => cb.checked);
        this.countTarget.innerText = `${selected.length}`;
        this.closeProductCategoryDropdown();

        const productIds = this.getProductAllowedCompanyIds();
        if (productIds.length > 0) {
            this.updateCompanyCategories(productIds);
        } else {
            this.updateCompanyCategories(null);
        }

        this.applyCombinedLocationFilter();
        this.updateMarkerVisibility();
        this.updateNoLocationsMessage();
    }

    applyCompanyCategories() {
        this.clearSearch();
        if (this.hasCompanyCategoryFilterSearchTarget) {
            this.companyCategoryFilterSearchTarget.value = '';
            this.filterCompanyCategoryItems();
        }

        const selected = this.hasCompanyCategoryCheckboxTarget
            ? this.companyCategoryCheckboxTargets.filter(cb => cb.checked)
            : [];

        if (this.hasCompanyCategoryCountTarget) {
            this.companyCategoryCountTarget.innerText = `${selected.length}`;
            if (selected.length > 0) {
                this.companyCategoryCountTarget.classList.remove('d-none');
            } else {
                this.companyCategoryCountTarget.classList.add('d-none');
            }
        }
        this.closeCompanyCategoryDropdown();

        const productIds = this.getProductAllowedCompanyIds();
        if (productIds.length > 0) {
            this.updateCompanyCategories(productIds);
        } else {
            this.updateCompanyCategories(null);
        }

        this.applyCombinedLocationFilter();
        this.updateMarkerVisibility();
        this.updateNoLocationsMessage();
    }

    resetProductCategoryUI() {
        if (this.hasCheckboxTarget) {
            this.checkboxTargets.forEach(cb => {
                cb.checked = false;
                cb.closest('.category-item')?.classList.remove('checked');
            });
        }
        if (this.hasCategoryFilterSearchTarget) {
            this.categoryFilterSearchTarget.value = '';
            this.filterCategoryItems();
        }
        if (this.hasCountTarget) {
            this.countTarget.innerText = '';
            this.countTarget.classList.add('d-none');
        }
    }

    resetCompanyCategories() {
        if (this.hasCompanyCategoryCheckboxTarget) {
            this.companyCategoryCheckboxTargets.forEach(cb => {
                cb.checked = false;
                cb.closest('.company-category-item')?.classList.remove('checked');
            });
        }
        if (this.hasCompanyCategoryFilterSearchTarget) {
            this.companyCategoryFilterSearchTarget.value = '';
        }
        if (this.hasCompanyCategoryCountTarget) {
            this.companyCategoryCountTarget.innerText = '';
            this.companyCategoryCountTarget.classList.add('d-none');
        }
    }

    updateCompanyCategories(allowlistCompanyIds) {
        if (!this.hasCompanyCategoryItemsContainerTarget) {
            return;
        }
        const items = this.companyCategoryItemsContainerTarget.querySelectorAll('.company-category-item');
        const allowSet =
            allowlistCompanyIds && allowlistCompanyIds.length > 0
                ? new Set(allowlistCompanyIds.map((id) => parseInt(id, 10)))
                : null;

        items.forEach((el) => {
            let show = true;
            if (allowSet) {
                try {
                    const ids = JSON.parse(el.dataset.companyIds || '[]');
                    show =
                        Array.isArray(ids) &&
                        ids.some((id) => allowSet.has(parseInt(id, 10)));
                } catch (e) {
                    show = false;
                }
            }
            el.dataset.filterNarrowed = show ? '0' : '1';
            el.style.display = show ? '' : 'none';
            if (!show) {
                const cb = el.querySelector('input[type="checkbox"]');
                if (cb) {
                    cb.checked = false;
                    el.classList.remove('checked');
                }
            }
        });
        this.filterCompanyCategoryItems();
        this.updateCompanyCategoryCount();
    }

    filterCompanyCategoryItems() {
        if (!this.hasCompanyCategoryItemsContainerTarget) {
            return;
        }
        const q = this.hasCompanyCategoryFilterSearchTarget
            ? this.companyCategoryFilterSearchTarget.value.trim().toLowerCase()
            : '';
        const items = this.companyCategoryItemsContainerTarget.querySelectorAll('.company-category-item');
        items.forEach((el) => {
            if (el.dataset.filterNarrowed === '1') {
                return;
            }
            const label = el.querySelector('.form-check-label')?.textContent?.trim().toLowerCase() || '';
            const match = !q || label.includes(q);
            el.style.display = match ? '' : 'none';
        });
    }

    filterCategoryItems() {
        if (!this.hasCategoryItemsContainerTarget) {
            return;
        }
        const q = this.hasCategoryFilterSearchTarget
            ? this.categoryFilterSearchTarget.value.trim().toLowerCase()
            : '';
        const items = this.categoryItemsContainerTarget.querySelectorAll('.category-item');
        let anyVisible = false;
        items.forEach((el) => {
            const label = el.querySelector('.form-check-label')?.textContent?.trim().toLowerCase() || '';
            const match = !q || label.includes(q);
            el.style.display = match ? '' : 'none';
            if (match) {
                anyVisible = true;
            }
        });
    }

    removeFilters(event) {
        event.stopPropagation();

        this.resetProductCategoryUI();
        this.closeProductCategoryDropdown();

        this.updateCompanyCategories(null);

        this.applyCombinedLocationFilter();
        this.reapplyLocationSearch();

        const categoryIds = this.getCompanyCategoryAllowedCompanyIds();

        if (categoryIds.length === 0) {
            this.locationElementTargets.forEach((el) => {
                el.classList.remove('location-active');
            });
            this.activeItem = null;
            const markersToReset = window.markers;
            if (markersToReset) {
                markersToReset.forEach(marker => {
                    marker.content = this.createDefaultPin();
                    marker.zIndex = 1;
                });
            }
            this.zoomOutMap();
        }

        this.updateMarkerVisibility();
        this.updateNoLocationsMessage();
    }

    removeCompanyFilters(event) {
        event.stopPropagation();

        if (this.hasCompanyCategoryFilterSearchTarget) {
            this.companyCategoryFilterSearchTarget.value = '';
        }

        if (this.hasCompanyCategoryCheckboxTarget) {
            this.companyCategoryCheckboxTargets.forEach(cb => {
                cb.checked = false;
                cb.closest('.company-category-item')?.classList.remove('checked');
            });
        }
        if (this.hasCompanyCategoryCountTarget) {
            this.companyCategoryCountTarget.innerText = '';
            this.companyCategoryCountTarget.classList.add('d-none');
        }
        this.closeCompanyCategoryDropdown();

        const productIds = this.getProductAllowedCompanyIds();

        if (productIds.length > 0) {
            this.updateCompanyCategories(productIds);
        } else {
            this.updateCompanyCategories(null);
        }

        this.applyCombinedLocationFilter();
        this.reapplyLocationSearch();

        if (productIds.length === 0) {
            this.locationElementTargets.forEach((el) => {
                el.classList.remove('location-active');
            });
            this.activeItem = null;
            const markersToReset = window.markers;
            if (markersToReset) {
                markersToReset.forEach(marker => {
                    marker.content = this.createDefaultPin();
                    marker.zIndex = 1;
                });
            }
            this.zoomOutMap();
        }

        this.filterCompanyCategoryItems();
        this.updateCompanyCategoryCount();
        this.updateNoLocationsMessage();
    }

    updateNoLocationsMessage() {
        let visibleCount = 0;

        this.locationElementTargets.forEach((item) => {
            const isExcluded =
                item.classList.contains('filter-excluded') ||
                item.classList.contains('search-excluded');
            if (!isExcluded && item.style.display !== 'none') {
                visibleCount++;
            }
        });

        const noLocMessage = document.getElementById('no-locations-message');
        if (!noLocMessage) {
            return;
        }

        if (visibleCount === 0) {
            noLocMessage.classList.add('d-block');
            noLocMessage.classList.remove('d-none');
        } else {
            noLocMessage.classList.add('d-none');
            noLocMessage.classList.remove('d-block');
        }
    }

    updateCount() {
        const selected = this.checkboxTargets.filter(cb => cb.checked);
        this.countTarget.innerText = `${selected.length}`;

        if (this.countTarget.innerText <= 0) {
            this.countTarget.classList.add('d-none');
        } else {
            this.countTarget.classList.remove('d-none');
        }
    }

    updateCompanyCategoryCount() {
        if (!this.hasCompanyCategoryCountTarget) {
            return;
        }
        const selected = this.hasCompanyCategoryCheckboxTarget
            ? this.companyCategoryCheckboxTargets.filter(cb => cb.checked)
            : [];
        this.companyCategoryCountTarget.innerText = `${selected.length}`;
        if (selected.length > 0) {
            this.companyCategoryCountTarget.classList.remove('d-none');
        } else {
            this.companyCategoryCountTarget.classList.add('d-none');
        }
    }

    updateMarkerVisibility() {
        const markers = (this.markers && this.markers.length) ? this.markers : (window.markers || []);
        const map = this.map || window.appMap;
        const clusterer = this.markerClusterer || window.markerClusterer;

        if (!map) {
            return;
        }

        markers.forEach(marker => {
            const sidebarItem = marker._sidebarItem;
            if (!sidebarItem) return;

            const isVisible = sidebarItem.style.display !== "none";
            const isExcluded = sidebarItem.classList.contains('search-excluded') || sidebarItem.classList.contains('filter-excluded');
            const shouldShow = isVisible && !isExcluded;

            if (clusterer) {
                if (shouldShow) {
                    clusterer.addMarker(marker);
                } else {
                    clusterer.removeMarker(marker);
                }
            } else if (shouldShow) {
                marker.setMap(map);
            } else {
                marker.setMap(null);
            }
        });
    }

    mobileCategories() {
        const topHeight = document.querySelector('.location-text-content')?.offsetHeight ?? 0;
        const windowWidth = window.innerWidth;

        const buttons = this.element.querySelectorAll('.product-categories, .company-categories');
        const dropdownContainer = this.element.querySelector('.dropdown-container');
        const baseTop = topHeight - 50;
        const tabletGap = 16;
        const connectorGap = 6.4;

        dropdownContainer?.style.removeProperty('--location-dropdown-menu-top');

        if (windowWidth >= 768 && windowWidth <= 1350) {
            let nextLeft = 40;
            buttons.forEach((btn) => {
                btn.style.position = 'absolute';
                btn.style.top = `${baseTop}px`;
                btn.style.left = `${nextLeft}px`;
                nextLeft += btn.offsetWidth + tabletGap;
            });

            const buttonHeight = buttons[0]?.offsetHeight ?? 0;
            dropdownContainer?.style.setProperty(
                '--location-dropdown-menu-top',
                `${baseTop + buttonHeight + connectorGap}px`
            );
        } else if (windowWidth < 768) {
            const searchBottom = this.searchBarTarget.getBoundingClientRect().bottom;
            const mapTop = this.mapTarget.getBoundingClientRect().top;
            const buttonsHeight = Array.from(buttons).reduce((height, btn) => height + btn.offsetHeight, 0);
            const availableSpace = mapTop - searchBottom - buttonsHeight;
            const equalGap = Math.max(0, availableSpace / (buttons.length + 1));
            const containerTop = dropdownContainer.getBoundingClientRect().top;
            let nextTop = searchBottom + equalGap;

            buttons.forEach((btn) => {
                btn.style.top = `${nextTop - containerTop}px`;
                btn.style.left = '24px';
                btn.style.position = 'absolute';
                nextTop += btn.offsetHeight + equalGap;
            });

            if (this.hasDropdownMenuTarget && this.dropdownMenuTarget.classList.contains('show')) {
                this.setDropdownMenuTop(this.dropdownToggleTarget);
            } else if (
                this.hasCompanyCategoryDropdownMenuTarget &&
                this.companyCategoryDropdownMenuTarget.classList.contains('show')
            ) {
                this.setDropdownMenuTop(this.companyCategoryDropdownToggleTarget);
            }
        } else {
            buttons.forEach((btn) => {
                btn.style.top = '';
                btn.style.left = '';
                btn.style.position = '';
            });
        }
    }

    zoomOutMap() {
        const map = this.map || window.appMap;
        const isMobile = window.innerWidth <= 768;
        if (map) {
            if (isMobile) {
                map.setCenter(new google.maps.LatLng(0, 0));
                const offsetY = this.mapTarget.offsetHeight * 0.1;
                map.setZoom(1);
                map.panBy(0, -offsetY);
                map.panBy(0, 40);
            } else {
                map.setCenter(new google.maps.LatLng(0, 0));
                map.setZoom(2);
            }
        }
    }

    matchesField(value, searchTerm) {
        if (!searchTerm) return true;
        if (!value) return false;
    
        const escapedTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`\\b${escapedTerm}`, 'i');
    
        return regex.test(value);
    }

    setDropdownMenuTop(activeButton) {
        if (window.innerWidth >= 768 || !activeButton) {
            return;
        }

        const dropdownContainer = this.element.querySelector('.dropdown-container');
        if (!dropdownContainer) {
            return;
        }

        const connectorGap = 6.4;
        const top = activeButton.offsetTop + activeButton.offsetHeight + connectorGap;
        dropdownContainer.style.setProperty('--location-dropdown-menu-top', `${top}px`);
    }

    closeProductCategoryDropdown() {
        if (!this.hasDropdownMenuTarget || !this.dropdownMenuTarget.classList.contains('show')) {
            return;
        }
        const inst = Dropdown.getInstance(this.dropdownToggleTarget);
        if (inst) {
            inst.hide();
        } else {
            this.dropdownMenuTarget.classList.remove('show');
            this.dropdownToggleTarget.classList.remove('show');
            this.dropdownToggleTarget.setAttribute('aria-expanded', 'false');
        }
    }

    closeCompanyCategoryDropdown() {
        if (
            !this.hasCompanyCategoryDropdownMenuTarget ||
            !this.hasCompanyCategoryDropdownToggleTarget ||
            !this.companyCategoryDropdownMenuTarget.classList.contains('show')
        ) {
            return;
        }
        const btn = this.companyCategoryDropdownToggleTarget;
        const inst = Dropdown.getInstance(btn);
        if (inst) {
            inst.hide();
        } else {
            this.companyCategoryDropdownMenuTarget.classList.remove('show');
            btn.classList.remove('show');
            btn.setAttribute('aria-expanded', 'false');
        }
    }

    toggleDropdown(event) {
        event.stopPropagation();
        this.closeCompanyCategoryDropdown();
        const button = this.dropdownToggleTarget;
        if (!button._dropdownInstance) {
            button._dropdownInstance = new Dropdown(button);
        }
        button._dropdownInstance.toggle();
        this.setDropdownMenuTop(button);
    }

    toggleCompanyDropdown(event) {
        event.stopPropagation();
        if (!this.hasCompanyCategoryDropdownToggleTarget) {
            return;
        }
        this.closeProductCategoryDropdown();
        const button = this.companyCategoryDropdownToggleTarget;
        if (!button._dropdownInstance) {
            button._dropdownInstance = new Dropdown(button);
        }
        button._dropdownInstance.toggle();
        this.setDropdownMenuTop(button);
    }

    handleOutsideClick(event) {
        if (this.hasDropdownMenuTarget &&
            !this.dropdownMenuTarget.contains(event.target) &&
            !this.dropdownToggleTarget.contains(event.target))
        {
            this.closeProductCategoryDropdown();
        }
        if (this.hasCompanyCategoryDropdownMenuTarget &&
            !this.companyCategoryDropdownMenuTarget.contains(event.target) &&
            !this.companyCategoryDropdownToggleTarget.contains(event.target))
        {
            this.closeCompanyCategoryDropdown();
        }
    }

    disconnect() {
        document.removeEventListener('click', this.boundHandleOutsideClick);
        if (this.boundMobileCategoriesResize) {
            window.removeEventListener('resize', this.boundMobileCategoriesResize);
        }
        if (this.onInitialized) {
            window.removeEventListener('UC_UI_INITIALIZED', this.onInitialized);
            window.removeEventListener('UC_UI_CMP_EVENT', this.onCmpEvent);
        }
        clearTimeout(this._mobileCategoriesResizeTimer);
    }

}
